"use client";

import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Icon } from "@repo/dashboard-core";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { getFileDownloadUrl } from "@/lib/actions/files";
import { triggerDownload } from "@/lib/utils/file-helpers";

interface DownloadButtonProps {
  fileName: string;
  onDownloadClick?: () => boolean;
}

export function DownloadButton({
  fileName,
  onDownloadClick,
}: DownloadButtonProps) {
  const t = useTranslations("my-files");
  const [loading, setLoading] = useState(false);

  async function handleDownload() {
    const deferred = onDownloadClick?.() ?? false;
    if (deferred) return;
    setLoading(true);
    try {
      const urlResult = await getFileDownloadUrl(fileName);
      if (!urlResult.success) {
        throw new Error(urlResult.message || t("error"));
      }
      const blob = await fetch(urlResult.data).then((r) => r.blob());
      triggerDownload(blob, fileName);
    } catch (err) {
      notifications.show({
        title: t("error"),
        message: err instanceof Error ? err.message : t("download-failed"),
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button
      variant="outline"
      color="orange"
      size="xs"
      className="hover:bg-[var(--mantine-color-orange-filled)] hover:text-white"
      loading={loading}
      leftSection={<Icon name="download" size={14} />}
      onClick={handleDownload}
    >
      {t("table.download")}
    </Button>
  );
}
